import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

export type StateTone = 'neutral' | 'info' | 'warning' | 'danger';

const TONE: Record<StateTone, { icon: string; iconBg: string; border: string }> = {
  neutral: {
    icon: 'var(--tx3)',
    iconBg: 'var(--elev)',
    border: 'var(--bd)',
  },
  info: {
    icon: 'var(--info)',
    iconBg: 'rgba(var(--info-rgb), 0.14)',
    border: 'rgba(var(--info-rgb), 0.32)',
  },
  warning: {
    icon: 'var(--warn)',
    iconBg: 'rgba(var(--warn-rgb), 0.14)',
    border: 'rgba(var(--warn-rgb), 0.34)',
  },
  danger: {
    icon: 'var(--err)',
    iconBg: 'rgba(var(--err-rgb), 0.14)',
    border: 'rgba(var(--err-rgb), 0.36)',
  },
};

interface StateBlockProps {
  icon: LucideIcon;
  title: string;
  description?: ReactNode;
  tone?: StateTone;
  /** Primary and secondary recovery actions, e.g. Retry / Go to Dashboard. */
  actions?: ReactNode;
  /** Raw technical detail — request id, stack, ObjectId. Kept monospaced. */
  detail?: string;
  className?: string;
}

/**
 * Shared layout for empty / error / permission-denied / not-found states, so
 * every one of them looks and behaves the same way. The brief asks for all four
 * on every list and detail surface.
 */
export function StateBlock({
  icon: Icon,
  title,
  description,
  tone = 'neutral',
  actions,
  detail,
  className,
}: StateBlockProps) {
  const palette = TONE[tone];

  return (
    <div
      className={cn(
        'bg-card flex flex-col items-center gap-3 rounded-xl border px-5 py-10 text-center',
        className,
      )}
      style={{ borderColor: palette.border }}
    >
      <span
        aria-hidden
        className="grid size-10 place-items-center rounded-lg"
        style={{ background: palette.iconBg, color: palette.icon }}
      >
        <Icon size={18} />
      </span>

      <h2
        className="font-head text-tx text-[17px] font-semibold uppercase"
        style={{ letterSpacing: '0.09em' }}
      >
        {title}
      </h2>

      {description && (
        <p className="text-tx2 max-w-[46ch] text-[13px] leading-[1.55]">{description}</p>
      )}

      {detail && (
        <code className="border-bd bg-bg-alt text-tx3 max-w-full overflow-x-auto rounded-md border px-2.5 py-1.5 font-mono text-[11px]">
          {detail}
        </code>
      )}

      {actions && <div className="mt-1 flex flex-wrap justify-center gap-2">{actions}</div>}
    </div>
  );
}
